import { useState, useContext, useRef } from 'react'
import * as colors from '@giphy/js-brand'
import { assertIsDefined } from 'shared/types/asserts'
import styled from 'styled-components'
import Captcha from 'shared/components/captcha'
import { GOOGLE_RECAPTCHA_KEY } from 'shared/variables'
import { TermsOfService } from 'shared/components/form/copy'
import AppleButton from 'shared/components/form/apple-button'
import FacebookButton from 'shared/components/form/facebook-button'
import { FormContainer, Content, CTAButton as BrandCTAButton } from 'shared/components/form/form-components'
import JoinFields, { FIELDS } from 'shared/components/form/fields'
import { createUser } from 'shared/api'
import MessageContext from 'shared/contexts/message'
import { FlashMessageContext } from 'shared/components/flash-message/flash-message'
import { each } from 'lodash'
import SnakeLoader from 'shared/components/snake-loader'
import { mobile } from '../../../shared/util/media-query'

const sectionMargin = 7

const FormContent = styled(Content)<{ width?: number }>`
    width: ${(props) => props.width}px;
`

const AppleWithMargin = styled(AppleButton)`
    margin-top: 8px;
    margin-bottom: ${sectionMargin}px;
`

const CaptchaWithMargin = styled(Captcha)`
    margin-bottom: ${sectionMargin}px;
`

const LoaderContainer = styled.div`
    position: absolute;
    width: 100%;
    bottom: 0;
    left: 0;
`

const CTAButton = styled(BrandCTAButton)`
    width: 100%;
    position: relative;
    background: linear-gradient(-135deg, #6157ff 0%, #9933ff 100%);
    opacity: ${(props) => (props.disabled ? '60' : '100')}% !important;
    font-size: 15px;
    border-radius: 3px;
    cursor: pointer;
    margin-bottom: 0;
`

const SignUpRecovery = styled.p`
    text-align: center;
    margin-top: -12px;
    margin-bottom: 4px;
    font-size: 14px !important;

    ${mobile.css`
    margin-top: -4px;
    margin-bottom: 12px;
  `}
`

const SignUpRecoveryLink = styled.button`
    background: none;
    border: none;
    display: inline-block;
    padding: 0;
    margin-left: 10px;
    font-size: 14px !important;
    color: white;
    font-weight: bold;
    font-family: inherit;
`

type Props = {
    width?: number
    onJoinSuccess?: (object) => void
    onFinishSignUpClick?: () => void
}

const JoinForm = ({ width, onJoinSuccess, onFinishSignUpClick }: Props) => {
    const { sendMessage } = useContext(MessageContext)
    const { showMessage } = useContext(FlashMessageContext)
    const formRef = useRef<HTMLFormElement | null>(null)
    const [isFormValid, setIsFormValid] = useState<boolean>(false)
    const [loading, setLoading] = useState<boolean>(false)
    const [isCaptchaSolved, setIsCaptchaSolved] = useState<boolean>(false)

    const areFieldsOkay = () => isFormValid && isCaptchaSolved
    const onValidChange = (valid) => {
        setIsFormValid(valid)
    }
    const onCaptchaChange = (responseToken) => {
        setIsCaptchaSolved(!!responseToken)
    }
    const onCaptchaError = () => {
        onCaptchaChange(false)
    }
    const submit = async (event) => {
        setLoading(true)
        event.stopPropagation()
        event.preventDefault()
        if (areFieldsOkay()) {
            assertIsDefined(formRef.current)
            const formData = new FormData(formRef.current)
            try {
                const response = await createUser({
                    username: formData.get('username'),
                    email: formData.get('email'),
                    password: formData.get('password'),
                    recaptcha_token: formData.get('g-recaptcha-response'),
                    platform: 'web',
                })

                if (onJoinSuccess) {
                    onJoinSuccess(response)
                }
            } catch (err) {
                if (err.details && err.details.code) {
                    showMessage({ slug: err.details.code })
                } else if (err.details) {
                    each(err.details, (values) => {
                        each(values, (value) => {
                            sendMessage(value, 'error')
                        })
                    })
                }
                setLoading(false)
                // @ts-ignore
                // this is sketcy, but the recaptcha we're using doesn't provide a ref we can access to do this
                window.grecaptcha.reset()
                setIsCaptchaSolved(false)
            }
        }
    }

    return (
        <FormContainer>
            <form onSubmit={submit} ref={formRef}>
                <FormContent width={width}>
                    <SignUpRecovery>
                        Already started creating an account?{' '}
                        <SignUpRecoveryLink onClick={onFinishSignUpClick} type="button">
                            Finish Sign Up
                        </SignUpRecoveryLink>
                    </SignUpRecovery>
                    <JoinFields
                        onValidChange={onValidChange}
                        showFields={[FIELDS.email, FIELDS.username, FIELDS.password, FIELDS.passwordConfirmation]}
                        borderRadius={3}
                    />
                    <CaptchaWithMargin
                        theme="dark"
                        size="normal"
                        sitekey={GOOGLE_RECAPTCHA_KEY}
                        onChange={onCaptchaChange}
                        onErrored={onCaptchaError}
                    />
                    <CTAButton
                        color={!areFieldsOkay() || loading ? colors.giphyDarkGrey : colors.primaryCTA}
                        disabled={!areFieldsOkay() || loading}
                    >
                        Sign Up
                        {loading && (
                            <LoaderContainer>
                                <SnakeLoader />
                            </LoaderContainer>
                        )}
                    </CTAButton>
                    <AppleWithMargin
                        label="Sign Up with Apple"
                        onClick={() => (location.href = `/login/apple/${location.search}`)}
                    />
                    <FacebookButton
                        label="Sign Up with Facebook"
                        onClick={() => (location.href = `/login/facebook/${location.search}`)}
                    />
                    <TermsOfService />
                </FormContent>
            </form>
        </FormContainer>
    )
}

export default JoinForm
